// مشترک صفحات BIL (AP-BIL-01): شناسه صفحات، کلیدهای Query، رنگ وضعیت مراجعه و صورتحساب و کمک‌های مبلغ و عدد.
import type {
  BilChequeStatus,
  BilInstallmentPlanStatus,
  BilInstallmentStatus,
  BilMembershipStatus,
  BilCashSessionStatus,
  BilPackageSaleStatus,
  BilQuoteStatus,
  BilSessionCreditStatus,
  BilDepositStatus,
  BilInvoiceStatus,
  BilMoney,
  BilPayerView,
  BilPaymentMethod,
  BilRefundStatus,
  BilVisitLineStatus,
  BilVisitStatus,
} from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { Money, StatusChip, type StatusTone } from '@phoenix/ui';

/** پذیرش و مراجعه (FLOW-04) و فهرست مراجعه‌های باز (C-AGENT-552). */
export const SCR_STAFF_400 = 'SCR-STAFF-400';
/** صورتحساب‌ها: جستجو، جزئیات، چاپ و ارسال. */
export const SCR_STAFF_403 = 'SCR-STAFF-403';
/** تنظیمات مالی: گردکردن، بستن خودکار، شماره‌گذاری، روش‌ها، سقف‌ها و صندوق‌ها (AP-BIL-02). */
export const SCR_STAFF_415 = 'SCR-STAFF-415';
/** صندوق: تسویه (FLOW-05). */
export const SCR_STAFF_401 = 'SCR-STAFF-401';
/** شیفت صندوق (FLOW-06). */
export const SCR_STAFF_402 = 'SCR-STAFF-402';
/** استردادها و تأییدها. */
export const SCR_STAFF_404 = 'SCR-STAFF-404';
/** بیعانه‌ها. */
export const SCR_STAFF_405 = 'SCR-STAFF-405';
/** پروفایل مالی مراجعه‌کننده. */
export const SCR_STAFF_408 = 'SCR-STAFF-408';
/** گزارش‌های مالی. */
export const SCR_STAFF_414 = 'SCR-STAFF-414';
/** صفحه عمومی پرداخت لینک. */
export const SCR_PAY_001 = 'SCR-PAY-001';

export const BIL_CASHIER_PATH = '/cashier';
export const BIL_SESSION_PATH = '/cashier/session';
export const BIL_REFUNDS_PATH = '/refunds';
export const BIL_DEPOSITS_PATH = '/deposits';
export const BIL_REPORTS_PATH = '/reports/cash';
export const checkoutPath = (visitId: string) => `${BIL_CASHIER_PATH}/checkout/${visitId}`;
export const cashierPatientPath = (patientId: string) => `${BIL_CASHIER_PATH}?patientId=${encodeURIComponent(patientId)}`;
export const financePath = (patientId: string) => `/patients/${patientId}/billing`;

/** روش‌های صندوق فاز 1a به ترتیب کلید میانبر FLOW-05 (F1 کارت، F2 نقد، F3 کیف پول، F4 انتقال، F5 چک — فاز 2، F6 لینک آنلاین). */
export const CASHIER_METHODS: readonly { method: BilPaymentMethod; key: string }[] = [
  { method: 'CardPos', key: 'F1' },
  { method: 'Cash', key: 'F2' },
  { method: 'Wallet', key: 'F3' },
  { method: 'BankTransfer', key: 'F4' },
  { method: 'Cheque', key: 'F5' },
  { method: 'OnlineGateway', key: 'F6' },
];

/** روش‌هایی که مرجع الزامی دارند. */
export const needsReference = (method: BilPaymentMethod) => method === 'CardPos' || method === 'BankTransfer';

export const BIL_VISITS_PATH = '/visits';
export const BIL_INVOICES_PATH = '/invoices';

export const visitPath = (id: string) => `${BIL_VISITS_PATH}/${id}`;
export const invoicePath = (id: string) => `${BIL_INVOICES_PATH}/${id}`;

export const bilKeys = {
  all: (tenantId?: string) => ['billing', tenantId] as const,
  visits: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'visits', filter] as const,
  visit: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'visit', id] as const,
  byAppointment: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'by-appointment', id] as const,
  lookups: (tenantId: string | undefined, id: string, q: string) => ['billing', tenantId, 'lookups', id, q] as const,
  invoices: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'invoices', filter] as const,
  invoice: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'invoice', id] as const,
  settings: (tenantId?: string) => ['billing', tenantId, 'settings'] as const,
  numbering: (tenantId?: string) => ['billing', tenantId, 'numbering'] as const,
  cashier: (tenantId: string | undefined, patientId: string, visitId: string) => ['billing', tenantId, 'cashier', patientId, visitId] as const,
  session: (tenantId?: string) => ['billing', tenantId, 'session'] as const,
  sessions: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'sessions', filter] as const,
  sessionDetail: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'session-detail', id] as const,
  registers: (tenantId?: string) => ['billing', tenantId, 'registers'] as const,
  refunds: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'refunds', filter] as const,
  deposits: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'deposits', filter] as const,
  finance: (tenantId: string | undefined, patientId: string) => ['billing', tenantId, 'finance', patientId] as const,
  report: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'report', filter] as const,
  publicPayment: (token: string) => ['billing', 'public', token] as const,
  sellable: (tenantId?: string) => ['billing', tenantId, 'sellable-packages'] as const,
  packageSales: (tenantId: string | undefined, patientId: string) => ['billing', tenantId, 'package-sales', patientId] as const,
  packageSale: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'package-sale', id] as const,
  quotes: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'quotes', filter] as const,
  quote: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'quote', id] as const,
  manualRules: (tenantId?: string) => ['billing', tenantId, 'manual-rules'] as const,
  deferred: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'deferred', filter] as const,
  // AP-BIL-04.
  pending: (tenantId?: string) => ['billing', tenantId, 'pending-payments'] as const,
  bankAccounts: (tenantId: string | undefined, branchId: string) => ['billing', tenantId, 'bank-accounts', branchId] as const,
  bankDeposits: (tenantId: string | undefined, registerId: string) => ['billing', tenantId, 'bank-deposits', registerId] as const,
  plans: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'installment-plans', filter] as const,
  plan: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'installment-plan', id] as const,
  cheques: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'cheques', filter] as const,
  reconciliations: (tenantId?: string) => ['billing', tenantId, 'reconciliations'] as const,
  reconciliation: (tenantId: string | undefined, id: string) => ['billing', tenantId, 'reconciliation', id] as const,
  membershipPlans: (tenantId?: string) => ['billing', tenantId, 'membership-plans'] as const,
  memberships: (tenantId: string | undefined, patientId: string) => ['billing', tenantId, 'memberships', patientId] as const,
  promotions: (tenantId?: string) => ['billing', tenantId, 'wallet-promotions'] as const,
  writeOffs: (tenantId: string | undefined, patientId: string) => ['billing', tenantId, 'write-offs', patientId] as const,
  products: (tenantId: string | undefined, q: string) => ['billing', tenantId, 'products', q] as const,
  receivables: (tenantId: string | undefined, filter: string) => ['billing', tenantId, 'receivables', filter] as const,
};

// AP-BIL-04: اقساط، چک، مغایرت‌گیری، فروش کالا و گزارش مطالبات.
/** اقساط و معوقات. */
export const SCR_STAFF_409 = 'SCR-STAFF-409';
/** چک‌ها. */
export const SCR_STAFF_410 = 'SCR-STAFF-410';
/** مغایرت‌گیری درگاه و کارتخوان. */
export const SCR_STAFF_412 = 'SCR-STAFF-412';
/** فروش کالا (صندوق سریع). */
export const SCR_STAFF_413 = 'SCR-STAFF-413';
/** صف صورتحساب‌های الکترونیک ناموفق (AP-BIL-05؛ US-BIL-023). */
export const SCR_STAFF_416 = 'SCR-STAFF-416';
/** حساب سازمان‌های طرف قرارداد (AP-BIL-05؛ US-BIL-120، 121). */
export const SCR_STAFF_411 = 'SCR-STAFF-411';

export const BIL_E_INVOICES_PATH = '/reports/e-invoices';
export const BIL_ORGANIZATIONS_PATH = '/organization-accounts';
export const BIL_INSTALLMENTS_PATH = '/installments';
export const BIL_CHEQUES_PATH = '/cheques';
export const BIL_RECONCILIATION_PATH = '/reconciliation';
export const BIL_PRODUCT_SALE_PATH = '/cashier/products';
export const BIL_RECEIVABLES_PATH = '/reports/receivables';
export const installmentsPath = (patientId?: string) => (patientId ? `${BIL_INSTALLMENTS_PATH}?patientId=${encodeURIComponent(patientId)}` : BIL_INSTALLMENTS_PATH);
export const chequesPath = (patientId?: string) => (patientId ? `${BIL_CHEQUES_PATH}?patientId=${encodeURIComponent(patientId)}` : BIL_CHEQUES_PATH);

/** تاریخ yyyy-MM-dd به‌علاوه روز (تقویم میلادی؛ نمایش شمسی در لبه). */
export function addDays(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

/** رنگ وضعیت چک. */
export function chequeTone(status: BilChequeStatus): StatusTone {
  switch (status) {
    case 'Received':
    case 'Deposited':
      return 'pending';
    case 'Cleared':
      return 'done';
    case 'Bounced':
      return 'error';
    case 'PendingSayyad':
      return 'booked';
    default:
      return 'inactive';
  }
}

export function ChequeStatusChip({ status }: { status: BilChequeStatus }) {
  return <StatusChip tone={chequeTone(status)} labelKey={`bil:cheque_status.${status}`} />;
}

/** رنگ وضعیت برنامه یا قسط. */
export function installmentTone(status: BilInstallmentPlanStatus | BilInstallmentStatus): StatusTone {
  switch (status) {
    case 'Active':
    case 'Pending':
      return 'pending';
    case 'Completed':
    case 'Paid':
      return 'done';
    case 'Overdue':
    case 'Defaulted':
      return 'error';
    default:
      return 'inactive';
  }
}

export function InstallmentStatusChip({ status }: { status: BilInstallmentPlanStatus | BilInstallmentStatus }) {
  return <StatusChip tone={installmentTone(status)} labelKey={`bil:installment_status.${status}`} />;
}

export function MembershipStatusChip({ status }: { status: BilMembershipStatus }) {
  const tone: StatusTone = status === 'Active' ? 'confirmed' : status === 'PendingPayment' ? 'pending' : 'inactive';
  return <StatusChip tone={tone} labelKey={`bil:membership_status.${status}`} />;
}

// AP-BIL-03: پکیج، برآورد و گزارش پیش‌دریافت.
/** فروش و مدیریت پکیج (FLOW-11). */
export const SCR_STAFF_406 = 'SCR-STAFF-406';
/** برآورد هزینه (FLOW-12). */
export const SCR_STAFF_407 = 'SCR-STAFF-407';
export const BIL_PACKAGES_PATH = '/packages';
export const BIL_QUOTES_PATH = '/quotes';
export const BIL_DEFERRED_PATH = '/reports/deferred-revenue';
export const packagesPath = (patientId?: string) => (patientId ? `${BIL_PACKAGES_PATH}?patientId=${encodeURIComponent(patientId)}` : BIL_PACKAGES_PATH);
export const quotePath = (id: string) => `${BIL_QUOTES_PATH}/${id}`;
export const newQuotePath = (patientId?: string) => (patientId ? `${BIL_QUOTES_PATH}/new?patientId=${encodeURIComponent(patientId)}` : `${BIL_QUOTES_PATH}/new`);

/** رنگ وضعیت فروش پکیج. */
export function packageTone(status: BilPackageSaleStatus): StatusTone {
  switch (status) {
    case 'Active':
      return 'confirmed';
    case 'PendingPayment':
      return 'pending';
    case 'Exhausted':
      return 'done';
    case 'Suspended':
      return 'inactive';
    default:
      return 'cancelled';
  }
}

export function PackageStatusChip({ status }: { status: BilPackageSaleStatus }) {
  return (
    <span data-testid="package-status" data-status={status}>
      <StatusChip tone={packageTone(status)} labelKey={`bil:package_status.${status}`} />
    </span>
  );
}

/** رنگ وضعیت اعتبار جلسه. */
export function creditTone(status: BilSessionCreditStatus): StatusTone {
  switch (status) {
    case 'Available':
      return 'confirmed';
    case 'Reserved':
      return 'booked';
    case 'Consumed':
      return 'done';
    case 'Inactive':
      return 'pending';
    default:
      return 'cancelled';
  }
}

export function CreditStatusChip({ status }: { status: BilSessionCreditStatus }) {
  return (
    <span data-testid="credit-status" data-status={status}>
      <StatusChip tone={creditTone(status)} labelKey={`bil:credit_status.${status}`} />
    </span>
  );
}

/** رنگ وضعیت برآورد. */
export function quoteTone(status: BilQuoteStatus): StatusTone {
  switch (status) {
    case 'Draft':
      return 'inactive';
    case 'Presented':
      return 'pending';
    case 'Accepted':
      return 'confirmed';
    case 'Converted':
      return 'done';
    default:
      return 'cancelled';
  }
}

export function QuoteStatusChip({ status }: { status: BilQuoteStatus }) {
  return (
    <span data-testid="quote-status" data-status={status}>
      <StatusChip tone={quoteTone(status)} labelKey={`bil:quote_status.${status}`} />
    </span>
  );
}

/** رنگ وضعیت شیفت. */
export function sessionTone(status: BilCashSessionStatus): StatusTone {
  switch (status) {
    case 'Open':
      return 'confirmed';
    case 'Closing':
      return 'pending';
    case 'Discrepancy':
      return 'no_show';
    case 'Approved':
      return 'done';
    default:
      return 'inactive';
  }
}

export function SessionStatusChip({ status }: { status: BilCashSessionStatus }) {
  return (
    <span data-testid="session-status" data-status={status}>
      <StatusChip tone={sessionTone(status)} labelKey={`bil:session_status.${status}`} />
    </span>
  );
}

/** رنگ وضعیت استرداد. */
export function refundTone(status: BilRefundStatus): StatusTone {
  switch (status) {
    case 'Completed':
      return 'done';
    case 'PendingApproval':
      return 'pending';
    case 'Processing':
    case 'Requested':
      return 'in_service';
    default:
      return 'cancelled';
  }
}

export function RefundStatusChip({ status }: { status: BilRefundStatus }) {
  return (
    <span data-testid="refund-status" data-status={status}>
      <StatusChip tone={refundTone(status)} labelKey={`bil:refund_status.${status}`} />
    </span>
  );
}

/** رنگ وضعیت بیعانه. */
export function depositTone(status: BilDepositStatus): StatusTone {
  switch (status) {
    case 'Paid':
    case 'Applied':
      return 'done';
    case 'Pending':
      return 'pending';
    case 'Refundable':
      return 'in_service';
    default:
      return 'cancelled';
  }
}

export function DepositStatusChip({ status }: { status: BilDepositStatus }) {
  return (
    <span data-testid="deposit-status" data-status={status}>
      <StatusChip tone={depositTone(status)} labelKey={`bil:deposit_status.${status}`} />
    </span>
  );
}

/** رنگ ثابت وضعیت مراجعه (SM-Visit). */
export function visitTone(status: BilVisitStatus): StatusTone {
  switch (status) {
    case 'Open':
    case 'Reopened':
      return 'confirmed';
    case 'InService':
      return 'in_service';
    case 'AwaitingPayment':
      return 'pending';
    case 'Closed':
      return 'done';
    default:
      return 'cancelled';
  }
}

export function VisitStatusChip({ status }: { status: BilVisitStatus }) {
  return (
    <span data-testid="visit-status" data-status={status}>
      <StatusChip tone={visitTone(status)} labelKey={`bil:visit_status.${status}`} />
    </span>
  );
}

/** رنگ وضعیت قلم. */
export function lineTone(status: BilVisitLineStatus): StatusTone {
  switch (status) {
    case 'Rendered':
      return 'done';
    case 'Planned':
      return 'booked';
    default:
      return 'cancelled';
  }
}

export function LineStatusChip({ status }: { status: BilVisitLineStatus }) {
  return (
    <span data-testid="line-status" data-status={status}>
      <StatusChip tone={lineTone(status)} labelKey={`bil:line_status.${status}`} />
    </span>
  );
}

/** رنگ وضعیت صورتحساب (SM-Invoice). */
export function invoiceTone(status: BilInvoiceStatus): StatusTone {
  switch (status) {
    case 'Paid':
      return 'done';
    case 'Issued':
    case 'PartiallyPaid':
      return 'pending';
    case 'Draft':
      return 'inactive';
    default:
      return 'cancelled';
  }
}

export function InvoiceStatusChip({ status }: { status: BilInvoiceStatus }) {
  return (
    <span data-testid="invoice-status" data-status={status}>
      <StatusChip tone={invoiceTone(status)} labelKey={`bil:invoice_status.${status}`} />
    </span>
  );
}

/** عدد از قرارداد (int ممکن است رشته باشد). */
export function num(value: number | string | null | undefined): number {
  if (value === null || value === undefined) {
    return 0;
  }
  return typeof value === 'number' ? value : Number(value);
}

/** مبلغ قرارداد (ریال صحیح — ADR-013). */
export function Amount({ money, testId }: { money: BilMoney | null | undefined; testId?: string }) {
  if (!money) {
    return <span>—</span>;
  }
  return (
    <span data-testid={testId} data-amount={money.amount}>
      <Money amountMinor={money.amount} currency={money.currency} />
    </span>
  );
}

export const rial = (amount: number): BilMoney => ({ amount, currency: 'IRR' });

/** نام نمایشی پرداخت‌کننده. */
export function usePayerLabel() {
  const { t } = useTranslation('bil');
  return (payer: BilPayerView | null | undefined) => {
    if (!payer || payer.kind === 'Self') {
      return t('bil:payer.self');
    }
    if (payer.kind === 'Organization') {
      return payer.organizationName ?? t('bil:payer.Organization');
    }
    return payer.displayName ?? t('bil:payer.Person');
  };
}

/** ذخیره Blob با نام فایل. */
export function saveBlob(blob: Blob, fileName: string) {
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
